import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { Sparkles, Copy, Loader2, ClipboardCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";

/**
 * "Explain this expense to my accountant" — BuildIntel-generated 2-3 sentence
 * blurb with a Copy button so the tradie can paste into an email.
 *
 * Two modes:
 *   - **Uncontrolled** (default): renders its own trigger button + sheet.
 *   - **Controlled**: parent passes `open` + `onOpenChange` and (optionally)
 *     `hideTrigger` — no trigger button is rendered. This mode is used from
 *     the Ledger row 3-dot menu, where the parent needs to close its
 *     DropdownMenu cleanly before opening the sheet. Nesting this component
 *     inside a `DropdownMenuItem asChild` used to break because Radix eats
 *     the click via its own `onSelect` flow.
 */
export default function ExplainEntryButton({
  entryId,
  supplier,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}) {
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (v) => {
    if (!isControlled) setUncontrolledOpen(v);
    onOpenChange?.(v);
  };

  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  // Track which entryId's explanation is currently held in state so a
  // controlled parent can re-open the sheet for a *different* row and we
  // fetch fresh copy instead of showing the old one.
  const [loadedFor, setLoadedFor] = useState(null);

  const load = async () => {
    if (!entryId) return;
    setLoading(true);
    setText("");
    setCopied(false);
    setLoadedFor(entryId);
    try {
      const { data } = await api.get(`/ai/explain-entry/${entryId}`);
      setText(data.explanation || "");
    } catch (e) {
      devLog.warn("explain failed", e);
      toast.error("Couldn't explain this expense right now");
    } finally {
      setLoading(false);
    }
  };

  // Kick off a fetch the first time the sheet opens for a given entry.
  // Works for BOTH modes — the uncontrolled trigger sets `open=true` which
  // triggers this effect, and the controlled parent flipping `open` does
  // exactly the same thing. Single code path, no divergence.
  useEffect(() => {
    if (!open || !entryId) return;
    if (loadedFor === entryId) return;
    load();
  }, [open, entryId]);

  const openViaTrigger = () => setOpen(true);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          data-testid={`explain-btn-${entryId}`}
          onClick={openViaTrigger}
          className="h-8 px-3 rounded-lg bi-ghost-btn text-[11px] inline-flex items-center gap-1.5 hover:border-[#FF9500]/40 transition"
          title="Explain this expense to my accountant"
        >
          <Sparkles className="w-3 h-3 text-[#FF9500]" /> Explain
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[#1F1F1F] border-white/10 text-white p-0">
          <SheetHeader className="px-5 pt-5 pb-2 text-left">
            <SheetTitle className="font-heading font-extrabold text-xl flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#FF9500]" /> Explain to accountant
            </SheetTitle>
            <SheetDescription className="text-white/60 text-sm">
              A plain-English summary of {supplier || "this expense"} you can email straight through.
            </SheetDescription>
          </SheetHeader>
          <div className="px-5 pt-4 pb-6 space-y-3">
            <div className="bi-surface p-4 min-h-[110px]" data-testid="explain-text">
              {loading && (
                <div className="text-white/60 inline-flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Writing a clean explanation…
                </div>
              )}
              {!loading && text && (
                <p className="text-[15px] leading-relaxed text-white/90">{text}</p>
              )}
              {!loading && !text && (
                <p className="text-white/50 text-sm">Tap retry to try again.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={load}
                disabled={loading}
                data-testid="explain-retry"
                className="h-14 rounded-xl bi-ghost-btn flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4" /> Rewrite
              </button>
              <button
                type="button"
                onClick={copyText}
                disabled={loading || !text}
                data-testid="explain-copy"
                className="h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
              >
                {copied ? <ClipboardCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
